import { StateCreator } from 'zustand';

import { NarrativeState } from '../../types/narrative';
import { PlanningState, initialPlanningState } from '../../types/planning';
import { initializeNarrativeState } from '../../utils/narrativeHelpers';
import {
  deserializeNarrativeState,
  serializeNarrativeState,
} from '../../utils/worldStateSerializer';
import { AppStore } from '../appStore';

import {
  FlowState,
  createInitialFlowState,
  deserializeFlowState,
  serializeFlowState,
} from './flowSlice';

export interface Project {
  id: string;
  name: string;
  createdAt: Date;
  lastModified: Date;
  completionPercentage: number;
  planningState: PlanningState;
  narrativeState: NarrativeState;
  flowState: FlowState;
  hasBPlotBeenCompleted: boolean;
  isLocalOnly?: boolean;
  syncStatus?: 'synced' | 'pending' | 'conflict' | 'error';
  lastSyncedAt?: Date;
}

export interface ProjectsSlice {
  projects: Record<string, Project>;
  currentProjectId: string | null;

  createProject: (name: string) => string;
  loadProject: (projectId: string) => void;
  saveCurrentProject: () => void;
  renameProject: (projectId: string, newName: string) => void;
  deleteProject: (projectId: string) => void;
  duplicateProject: (projectId: string, newName: string) => string;
  calculateCompletionPercentage: (project: Project) => number;
  initializeProjects: () => void;
  setProjectSyncFailed: (projectId: string, failed: boolean) => void;
  updateProjectSyncStatus: (
    projectId: string,
    status: 'synced' | 'pending' | 'conflict' | 'error'
  ) => void;
  mergeProjects: (incoming: Record<string, Project>) => void;
}

const PROJECTS_KEY = 'app-projects';
const CURRENT_ID_KEY = 'app-current-project-id';

function loadProjectsFromStorage(): Record<string, Project> {
  try {
    const stored = localStorage.getItem(PROJECTS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, Project>;
      Object.values(parsed).forEach((p: any) => {
        // Ensure dates are properly converted
        p.createdAt = p.createdAt ? new Date(p.createdAt) : new Date();
        p.lastModified = p.lastModified ? new Date(p.lastModified) : new Date();

        // Validate dates - if invalid, use current date
        if (isNaN(p.createdAt.getTime())) {
          p.createdAt = new Date();
        }
        if (isNaN(p.lastModified.getTime())) {
          p.lastModified = new Date();
        }

        if (p.flowState && !(p.flowState.completedSteps instanceof Set)) {
          p.flowState = deserializeFlowState(p.flowState);
        }

        // Deserialize narrative state
        if (p.narrativeState) {
          p.narrativeState = deserializeNarrativeState(p.narrativeState);
        }

        if (typeof p.isLocalOnly !== 'boolean') {
          p.isLocalOnly = false;
        }
      });
      return parsed;
    }
  } catch (e) {
    console.error('Failed to load projects', e);
    try {
      localStorage.removeItem(PROJECTS_KEY);
    } catch {
      /* ignore */
    }
  }
  return {};
}

function saveProjectsToStorage(projects: Record<string, Project>) {
  try {
    const serializable: Record<string, any> = {};
    Object.entries(projects).forEach(([id, p]) => {
      serializable[id] = {
        ...p,
        flowState: serializeFlowState(p.flowState),
        narrativeState: serializeNarrativeState(p.narrativeState),
      };
    });
    localStorage.setItem(PROJECTS_KEY, JSON.stringify(serializable));
  } catch (e) {
    console.error('Failed to save projects', e);
  }
}

function loadCurrentProjectId(): string | null {
  try {
    return localStorage.getItem(CURRENT_ID_KEY);
  } catch {
    return null;
  }
}

function saveCurrentProjectId(id: string | null) {
  try {
    if (id) localStorage.setItem(CURRENT_ID_KEY, id);
    else localStorage.removeItem(CURRENT_ID_KEY);
  } catch (e) {
    console.error('Failed to save current project id', e);
  }
}

export const createProjectsSlice: StateCreator<AppStore, [], [], ProjectsSlice> = (set, get) => ({
  projects: loadProjectsFromStorage(),
  currentProjectId: loadCurrentProjectId(),

  createProject: (name) => {
    const id = (crypto as any).randomUUID?.() || Math.random().toString(36).slice(2);
    const project: Project = {
      id,
      name,
      createdAt: new Date(),
      lastModified: new Date(),
      completionPercentage: 0,
      planningState: initialPlanningState,
      narrativeState: initializeNarrativeState(),
      flowState: createInitialFlowState(),
      hasBPlotBeenCompleted: false,
      isLocalOnly: false,
      syncStatus: 'pending',
      lastSyncedAt: undefined,
    };
    set((state: AppStore) => ({
      projects: { ...state.projects, [id]: project },
      currentProjectId: id,
      planningState: project.planningState,
      narrativeState: project.narrativeState,
      flow: project.flowState,
      hasBPlotBeenCompleted: project.hasBPlotBeenCompleted,
      hasUnsavedChanges: true,
    }));
    saveProjectsToStorage(get().projects);
    saveCurrentProjectId(id);
    return id;
  },

  loadProject: (projectId) => {
    const project = get().projects[projectId];
    if (!project) return;
    set(() => ({
      currentProjectId: projectId,
      planningState: project.planningState,
      narrativeState: project.narrativeState,
      flow: project.flowState,
      hasBPlotBeenCompleted: project.hasBPlotBeenCompleted,
      hasUnsavedChanges: false,
    }));
    saveCurrentProjectId(projectId);

    // Navigate to the flow step saved in the project
    const { flowActions } = get();
    const targetStep = project.flowState.currentStep;
    if (flowActions.canNavigateToStep(targetStep)) {
      flowActions.goToStep(targetStep);
    } else {
      // If we can't navigate to the saved step, go to the highest allowed step
      flowActions.goToStep(project.flowState.maxAllowedStep);
    }
  },

  saveCurrentProject: () => {
    const id = get().currentProjectId;
    if (!id) return;
    const state = get();
    const existing = state.projects[id];
    if (!existing) return;
    const updated: Project = {
      ...existing,
      planningState: state.planningState,
      narrativeState: state.narrativeState,
      flowState: state.flow,
      hasBPlotBeenCompleted: state.hasBPlotBeenCompleted,
      lastModified: new Date(),
      syncStatus: 'pending',
    };
    updated.completionPercentage = state.calculateCompletionPercentage(updated);
    set({ projects: { ...state.projects, [id]: updated }, hasUnsavedChanges: false });
    saveProjectsToStorage(get().projects);
  },

  renameProject: (projectId, newName) => {
    set((state) => {
      const project = state.projects[projectId];
      if (!project) return {} as any;
      const updated = { ...project, name: newName, lastModified: new Date() };
      return { projects: { ...state.projects, [projectId]: updated }, hasUnsavedChanges: true };
    });
    saveProjectsToStorage(get().projects);
  },

  deleteProject: (projectId) => {
    set((state: AppStore) => {
      const newProjects = { ...state.projects };
      delete newProjects[projectId];
      let newCurrent = state.currentProjectId;
      if (newCurrent === projectId) {
        newCurrent = Object.keys(newProjects)[0] || null;
      }
      return {
        projects: newProjects,
        currentProjectId: newCurrent,
        hasUnsavedChanges: true,
      } as any;
    });
    const current = get().currentProjectId;
    if (current) {
      const proj = get().projects[current];
      if (proj) {
        set({
          planningState: proj.planningState,
          narrativeState: proj.narrativeState,
          flow: proj.flowState,
          hasBPlotBeenCompleted: proj.hasBPlotBeenCompleted,
        });
      }
    }
    saveProjectsToStorage(get().projects);
    saveCurrentProjectId(get().currentProjectId);
  },

  duplicateProject: (projectId, newName) => {
    const source = get().projects[projectId];
    if (!source) return '';
    const id = (crypto as any).randomUUID?.() || Math.random().toString(36).slice(2);
    const duplicate: Project = {
      ...source,
      id,
      name: newName,
      createdAt: new Date(),
      lastModified: new Date(),
      isLocalOnly: source.isLocalOnly,
    };
    set((state) => ({ projects: { ...state.projects, [id]: duplicate } }));
    set({ hasUnsavedChanges: true });
    saveProjectsToStorage(get().projects);
    return id;
  },

  setProjectSyncFailed: (projectId, failed) => {
    set((state) => {
      const project = state.projects[projectId];
      if (!project) return {} as any;
      return {
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            isLocalOnly: failed,
            syncStatus: failed ? 'error' : 'synced',
            lastSyncedAt: failed ? project.lastSyncedAt : new Date(),
          },
        },
      } as any;
    });
    saveProjectsToStorage(get().projects);
  },

  updateProjectSyncStatus: (projectId, status) => {
    set((state) => {
      const project = state.projects[projectId];
      if (!project) return {} as any;
      return {
        projects: {
          ...state.projects,
          [projectId]: {
            ...project,
            syncStatus: status,
            lastSyncedAt: status === 'synced' ? new Date() : project.lastSyncedAt,
          },
        },
      } as any;
    });
    saveProjectsToStorage(get().projects);
  },

  mergeProjects: (incoming) => {
    set((state: AppStore) => {
      const merged = { ...state.projects } as Record<string, Project>;
      Object.entries(incoming).forEach(([id, project]) => {
        const local = merged[id];
        if (!local) {
          merged[id] = {
            ...project,
            isLocalOnly: false,
            syncStatus: 'synced',
            lastSyncedAt: project.lastModified,
          } as Project;
          return;
        }

        const localTime = local.lastModified.getTime();
        const remoteTime = project.lastModified.getTime();

        if (localTime > remoteTime && local.isLocalOnly) {
          // conflict - keep local but mark
          merged[id] = { ...local, syncStatus: 'conflict' } as Project;
        } else if (remoteTime > localTime) {
          merged[id] = {
            ...project,
            isLocalOnly: false,
            syncStatus: 'synced',
            lastSyncedAt: project.lastModified,
          } as Project;
        } else {
          merged[id] = {
            ...local,
            isLocalOnly: false,
            syncStatus: 'synced',
            lastSyncedAt: project.lastModified,
          } as Project;
        }
      });
      return { projects: merged } as any;
    });
    saveProjectsToStorage(get().projects);
  },

  calculateCompletionPercentage: (project) => {
    const nodes = Object.values(project.narrativeState.nodes).filter(
      (n) => n.type !== 'Structural' && n.type !== 'Terminal'
    );
    const total = nodes.length;
    const completed = nodes.filter((n) => n.completionStatus === 'complete').length;
    return total === 0 ? 0 : Math.round((completed / total) * 100);
  },

  initializeProjects: () => {
    const state = get();
    if (Object.keys(state.projects).length > 0) {
      if (state.currentProjectId) {
        state.loadProject(state.currentProjectId);
      } else {
        const firstId = Object.keys(state.projects)[0];
        if (firstId) state.loadProject(firstId);
      }
    }
    // Note: No automatic project creation - users start with empty project list
  },
});
